import { isFinn } from '@borealis/aurora';
import { type AdInputLookup, type EnrichedAdInputField, getAt } from '@finn-no/mobility-item-insertion-shared';
import type { Locale } from '@finn-no/number-format';
import { Trans } from '@lingui/react/macro';
import '@warp-ds/components/icon';
import { Box } from '@warp-ds/react';
import {
  type Control,
  Controller,
  type FieldError,
  type FieldErrors,
  type FieldValues,
  type RegisterOptions,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import type { ProfileWidgetModel } from '../../../types/widget.js';
import { getHuckConfig } from '../../utils/getHuckConfig.js';
import { convertValidationRulesToRHFRules } from '../../utils.js';
import { EmbeddedPodlet } from '../embedded-podlet.js';
import { TextFieldWidget } from './textfield-widget.js';

type ProfileWidgetProps = Omit<ProfileWidgetModel, 'type'> & {
  adInputLookup: AdInputLookup;
  locale: Locale;
};

const getFieldProps = (adInputLookup: AdInputLookup, errors: FieldErrors, fieldName: string) => {
  const field = getAt<EnrichedAdInputField>(adInputLookup, `contact.children.${fieldName}`);
  const rules = field && 'rules' in field && field.rules ? convertValidationRulesToRHFRules(field.rules) : undefined;
  const error = getAt(errors, `contact.${fieldName}`) as FieldError | undefined;
  const helpText = error?.message;
  return { field, rules, error, helpText };
};

const renderTextField = (
  name: string,
  control: Control<FieldValues, unknown>,
  fieldProps: {
    field: EnrichedAdInputField | undefined;
    rules: Pick<RegisterOptions, 'max' | 'min' | 'maxLength' | 'minLength' | 'required'> | undefined;
    error: FieldError | undefined;
    helpText: string | undefined;
  },
  locale: Locale,
) => (
  <Controller
    name={`contact.${name}`}
    control={control}
    rules={fieldProps.rules}
    render={({ field }) => (
      <TextFieldWidget
        {...field}
        type="string"
        invalid={!!fieldProps.error}
        label={fieldProps.field?.label}
        helpText={fieldProps.helpText}
        mandatory={fieldProps.field?.mandatory}
        className="mb-32"
        locale={locale}
      />
    )}
  />
);

export const ProfileWidget = ({ adInputLookup, locale, className }: ProfileWidgetProps) => {
  const config = getHuckConfig();
  const {
    control,
    formState: { errors },
  } = useFormContext();
  const [username, email] = useWatch({
    name: ['contact.contact_name', 'contact.contact_email'],
  });

  /**
   * Certain models like caravan is used both in private and
   * business context. In business context fetching user info doesn't
   * work as intended, and we should make the contact fields editable in these
   * cases. If we enable manual edits for private users as well we can unblock
   * them in cases where fetching user info fails (should not happen though).
   * All of this is scoped for FINN, as that is the only place where these
   * fields are required and where we expect organisational users.
   */
  const contactNameProps = getFieldProps(adInputLookup, errors, 'contact_name');
  const contactEmailProps = getFieldProps(adInputLookup, errors, 'contact_email');

  // If the user is a business user (has orgId) or if there are errors on the contact fields, we allow editing the profile info.
  const isProfileEditable =
    (isFinn(config.brand) && !!config.orgId) || !!contactEmailProps.error || !!contactNameProps.error;

  const privateUserContent = (
    <EmbeddedPodlet content={config.adProfileCardContent} className={`${className} p-16 border rounded-8 mt-24`} />
  );
  const orgUserContent = (
    <Box info className={className}>
      <div className="flex mb-16 items-center gap-16">
        <w-icon name="CircleUser" size="large" locale={config.locale} />
        <div>
          <div className="font-bold">{username}</div>
          <div className="font-bold">{email}</div>
        </div>
      </div>
      <p className="text-xs">
        <Trans id="mobility-item-insertion.profile.disclaimer">
          If this profile is not correct, you can change it in your Vend Account, but be aware that this will also
          change the profile on all websites that use the Vend Account.
        </Trans>
      </p>
    </Box>
  );
  const nonEditableContent = config.orgId ? orgUserContent : privateUserContent;

  return isProfileEditable ? (
    <>
      {renderTextField('contact_email', control, contactEmailProps, locale)}
      {renderTextField('contact_name', control, contactNameProps, locale)}
    </>
  ) : (
    nonEditableContent
  );
};
